// Account settings + subscriptions page

function postAjax<T = any>(url: string, data: Record<string, any>): Promise<T> {
    return new Promise((resolve, reject) => {
        $.ajax({
            url, type: 'POST', data, dataType: 'json',
            success: resolve,
            error: (xhr) => {
                try { reject(JSON.parse(xhr.responseText)); }
                catch { reject({ message: 'Network error. Please try again.' }); }
            },
        });
    });
}

function getAjax<T = any>(url: string): Promise<T> {
    return new Promise((resolve, reject) => {
        $.ajax({
            url, type: 'GET', dataType: 'json',
            success: resolve,
            error: (xhr) => {
                try { reject(JSON.parse(xhr.responseText)); }
                catch { reject({ message: 'Network error. Please try again.' }); }
            },
        });
    });
}

function formData(form: JQuery): Record<string, any> {
    const out: Record<string, any> = {};
    form.find('input, select, textarea').each(function () {
        const el = this as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (!el.name || el.disabled) return;
        const type = (el as HTMLInputElement).type?.toLowerCase();
        if (type === 'checkbox' || type === 'radio') {
            if ((el as HTMLInputElement).checked) out[el.name] = el.value;
        } else {
            out[el.name] = el.value;
        }
    });
    return out;
}

function showBanner(id: string, msg: string, isError = true): void {
    const el = $(`#${id}`);
    el.removeClass('red-gradient green-gradient')
      .addClass(isError ? 'red-gradient' : 'green-gradient')
      .html(msg).show();
    $('html, body').animate({ scrollTop: (el.offset()?.top ?? 0) - 20 }, 250);
}

function showFieldErrors(errors: Record<string, string>): void {
    Object.entries(errors).forEach(([field, msg]) => {
        const input = $(`[name="${field}"]`);
        const li = input.closest('li');
        li.addClass('has-error');
        let errEl = li.find('.field-error');
        if (!errEl.length) errEl = $('<span class="field-error"></span>').appendTo(li);
        errEl.text(msg).show();
    });
    const first = $('.has-error').first();
    if (first.length) $('html, body').animate({ scrollTop: (first.offset()?.top ?? 0) - 80 }, 250);
}

function clearFieldErrors(): void {
    $('.field-error').text('').hide();
    $('.has-error').removeClass('has-error');
}

function setLoading(btn: JQuery, loading: boolean): void {
    if (loading) {
        if (!btn.data('orig-text')) btn.data('orig-text', btn.text());
        btn.prop('disabled', true).text('Please wait…');
    } else {
        btn.prop('disabled', false).text(btn.data('orig-text') || btn.text());
    }
}

function fmtDate(d: string | null | undefined): string {
    if (!d || d === '0000-00-00' || d === '0000-00-00 00:00:00') return '—';
    const dt = new Date(d.replace(' ', 'T'));
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtAmt(a: string | number | null | undefined): string {
    const n = parseFloat(String(a ?? '0'));
    return isNaN(n) ? '—' : '$' + n.toFixed(2);
}

// ---- Subscriptions tab ----

let subsLoaded = false;
const loadedCharges = new Set<string>();

interface Subscription {
    subscription_id: string;
    plan_id: string;
    plan_name: string;
    active: string;
    start_date: string;
    cancel_date: string;
    next_charge: string;
    last_charge: string;
    amount: string;
    card_last_four: string;
    created_at: string;
}

interface Charge {
    order_id: string;
    amount: string;
    timestamp: string;
    status: string;
    refunded: string;
    card_last_four: string;
}

function renderSubscriptions(subs: Subscription[]): void {
    const list = $('#subs-list');
    if (!subs.length) {
        list.html('<p>No subscription history found.</p>');
        return;
    }

    let rows = '';
    subs.forEach(s => {
        const isActive = s.active === '1';
        const badge = isActive
            ? '<span class="badge badge-active">Active</span>'
            : '<span class="badge badge-cancelled">Cancelled</span>';

        const nextCol = isActive
            ? fmtDate(s.next_charge)
            : (s.cancel_date && s.cancel_date !== '0000-00-00 00:00:00'
                ? 'Cancelled ' + fmtDate(s.cancel_date)
                : '—');

        let actions = '';
        if (isActive) {
            actions += `<button class="button sub-cancel-btn" type="button" data-pid="${s.subscription_id}" style="margin-right:4px">Cancel</button>`;
            if (s.plan_id === '1014') {
                actions += `<a class="button blue-gradient" href="/upgrade/1015">Upgrade to Premium</a>`;
            }
        }

        rows += `
        <tr class="sub-row" data-sub-id="${s.subscription_id}">
          <td><span class="expand-arrow">&#9658;</span>${s.plan_name || 'Plan ' + s.plan_id}</td>
          <td>${badge}</td>
          <td>${fmtDate(s.start_date)}</td>
          <td>${fmtAmt(s.amount)}/mo</td>
          <td>${nextCol}</td>
          <td>${actions}</td>
        </tr>
        <tr class="charges-row" id="charges-row-${s.subscription_id}" style="display:none">
          <td colspan="6">
            <div class="charges-inner" id="charges-inner-${s.subscription_id}">
              <em>Loading&hellip;</em>
            </div>
          </td>
        </tr>`;
    });

    list.html(`
      <table class="subs-table">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Status</th>
            <th>Started</th>
            <th>Amount</th>
            <th>Next Charge / Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`);
}

function renderCharges(subId: string, charges: Charge[]): void {
    const inner = $(`#charges-inner-${subId}`);
    if (!charges.length) {
        inner.html('<em>No transactions found.</em>');
        return;
    }

    let rows = '';
    charges.forEach(c => {
        const refunded  = c.refunded === '1';
        const succeeded = c.status === '1';
        let statusLabel: string;
        let statusClass: string;
        if (refunded) {
            statusLabel = 'Refunded'; statusClass = '';
        } else if (succeeded) {
            statusLabel = 'Paid'; statusClass = 'charge-paid';
        } else {
            statusLabel = 'Failed'; statusClass = 'charge-failed';
        }
        const card = c.card_last_four ? `••••${c.card_last_four}` : '—';
        rows += `<tr>
          <td>${fmtDate(c.timestamp)}</td>
          <td>${fmtAmt(c.amount)}</td>
          <td>${card}</td>
          <td class="${statusClass}">${statusLabel}</td>
        </tr>`;
    });

    inner.html(`<table>
      <thead><tr><th>Date</th><th>Amount</th><th>Card</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`);
}

async function loadSubscriptions(): Promise<void> {
    if (subsLoaded) return;
    $('#subs-loading').show();

    try {
        const res = await getAjax<{ success: boolean; subscriptions: Subscription[]; message?: string }>('/settings/subscriptions');
        if (!res.success) {
            showBanner('subs-banner', res.message || 'Could not load subscriptions.');
            return;
        }
        renderSubscriptions(res.subscriptions);
        subsLoaded = true;
    } catch (e: any) {
        showBanner('subs-banner', e.message || 'Could not load subscriptions.');
    } finally {
        $('#subs-loading').hide();
    }
}

async function loadCharges(subId: string): Promise<void> {
    if (loadedCharges.has(subId)) return;
    loadedCharges.add(subId);

    try {
        const res = await getAjax<{ success: boolean; charges: Charge[]; message?: string }>(`/settings/charges?sub_id=${subId}`);
        if (!res.success) {
            $(`#charges-inner-${subId}`).html(`<em>${res.message || 'Could not load transactions.'}</em>`);
            return;
        }
        renderCharges(subId, res.charges);
    } catch (e: any) {
        $(`#charges-inner-${subId}`).html(`<em>${(e as any).message || 'Error loading transactions.'}</em>`);
    }
}

// ---- Main init ----

export function initSettings(state: string): void {
    ($('.datepicker') as any).glDatePicker({ zIndex: 100 });

    if (state) $('#state').val(state);

    // Tab switching
    $('.stab-btn').on('click', function () {
        const tab = $(this).data('tab') as string;
        $('.stab').hide();
        $(`#tab-${tab}`).show();
        $('.stab-btn').removeClass('active');
        $(this).addClass('active');
        if (tab === 'subs') loadSubscriptions();
    });

    // Settings form submit
    $('#settings-submit').on('click', async function () {
        const btn = $(this);
        $('#settings-banner').hide();
        clearFieldErrors();
        setLoading(btn, true);

        try {
            const data = formData($('#settings'));
            const res = await postAjax('/settings/save', data);

            if (!res.success) {
                if (res.errors) showFieldErrors(res.errors);
                showBanner('settings-banner', res.message || 'Please correct the errors below.');
                return;
            }
            showBanner('settings-banner', res.message || 'Settings updated.', false);
        } catch (e: any) {
            showBanner('settings-banner', e.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(btn, false);
        }
    });

    // Subscription row expand/collapse
    $(document).on('click', '.sub-row', function (e) {
        if ($(e.target).closest('button, a').length) return;
        const subId = $(this).data('sub-id') as string;
        const chargesRow = $(`#charges-row-${subId}`);
        const isOpen = chargesRow.is(':visible');

        if (!isOpen) {
            loadCharges(subId);
            chargesRow.show();
            $(this).addClass('expanded');
        } else {
            chargesRow.hide();
            $(this).removeClass('expanded');
        }
    });

    // Cancel subscription
    $(document).on('click', '.sub-cancel-btn', async function (e) {
        e.stopPropagation();
        if (!confirm('Are you sure you want to cancel this subscription?')) return;

        const btn = $(this);
        const pid = btn.data('pid') as string;
        btn.prop('disabled', true).text('Cancelling…');

        try {
            const res = await postAjax('/settings/cancel', { pid });
            if (!res.success) {
                showBanner('subs-banner', res.message || 'Cancellation failed.');
                btn.prop('disabled', false).text('Cancel');
                return;
            }
            showBanner('subs-banner', res.message || 'Subscription cancelled.', false);
            // Reload the subscription list
            subsLoaded = false;
            loadedCharges.clear();
            $('#subs-list').empty();
            loadSubscriptions();
        } catch (e: any) {
            showBanner('subs-banner', (e as any).message || 'An error occurred.');
            btn.prop('disabled', false).text('Cancel');
        }
    });
}
